import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ExternalLink, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SendNotificationDialog } from '@/components/issues/SendNotificationDialog';
import type { ReportedIssue } from '@/types';

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

interface RecentIssuesProps {
  issues: ReportedIssue[];
}

export function RecentIssues({ issues }: RecentIssuesProps) {
  const navigate = useNavigate();
  const [selectedIssue, setSelectedIssue] = useState<ReportedIssue | null>(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const handleSendNotification = (issue: ReportedIssue, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIssue(issue);
    setNotificationDialogOpen(true);
  };

  const handleIssueClick = (issue: ReportedIssue) => {
    navigate(`/admin/reported-issues`, { 
      state: { selectedIssueId: issue.id }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Issues</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {issues.map((issue) => (
            <div
              key={issue.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent cursor-pointer"
              onClick={() => handleIssueClick(issue)}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-semibold">#{issue.issue_number}</code>
                  <Badge
                    variant="outline"
                    className={`${statusColors[issue.status as keyof typeof statusColors]}`}
                  >
                    {issue.status}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  {issue.school?.name} • {issue.grade?.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(issue.created_at!), 'PPp')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleSendNotification(issue, e)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Mail className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      {selectedIssue && (
        <SendNotificationDialog
          issue={selectedIssue}
          open={notificationDialogOpen}
          onClose={() => {
            setNotificationDialogOpen(false);
            setSelectedIssue(null);
          }}
        />
      )}
    </Card>
  );
}